import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { BaseSelectField } from 'common/components/base-select-field';
import FormProvider from 'common/components/form-provider';
import React, { useEffect } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import { colors } from 'common/constants';
import { handleError } from 'utils/helpers';
import {
    compareQuestionData,
    getListQuestionFilter,
} from 'services/campaign.service';
import { useParams } from 'react-router-dom';
import { IListQuestionFilterResponse } from 'types/campaign';
import { useAppDispatch } from 'common/hook';
import { setListQuestionDataFilter } from 'slices/questionSummarySlice';
import { setIsLoading } from 'slices/loadingSlice';
import { campaignDataAnalysisCompareSchema } from 'common/schema';

const AnswersFetcher: React.FC<{
    questionId: number;
    index: number;
    listQuestionsFilter: IListQuestionFilterResponse[];
}> = ({ questionId, index, listQuestionsFilter }) => {
    const { setValue } = useFormContext();
    useEffect(() => {
        if (!questionId) return;
        setValue(`questions.${index}.answers`, []);
        setValue(`questions.${index}.availableAnswers`, []);
        const fetchAnswers = async () => {
            const question = listQuestionsFilter.find(
                (item) => item.attributes.id === questionId
            );
            setValue(
                `questions.${index}.availableAnswers`,
                question?.attributes.answer_options.map((item) => {
                    return {
                        label: item.text,
                        value: item.id,
                    };
                }),
                {
                    shouldValidate: true,
                }
            );
            setValue(
                `questions.${index}.questionType`,
                question?.attributes.kind
            );
            setValue(`questions.${index}.questionId`, questionId);
        };
        fetchAnswers();
    }, [questionId, index, setValue, listQuestionsFilter]);
    return null;
};
const Compare: React.FC = () => {
    const dispatch = useAppDispatch();
    const campaignId = useParams<{ id: string }>().id;
    const [listQuestionsFilter, setListQuestionsFilter] =
        React.useState<IListQuestionFilterResponse[]>();
    const methods = useForm({
        resolver: yupResolver(campaignDataAnalysisCompareSchema),
        defaultValues: {
            questions: [
                {
                    questionType: '',
                    questionId: '',
                    answers: [],
                },
            ],
        },
        mode: 'onSubmit',
    });
    useEffect(() => {
        const fetchListQuestions = async () => {
            try {
                const response = await getListQuestionFilter(
                    Number(campaignId),
                    'compare'
                );
                if (response.success) {
                    setListQuestionsFilter(response.data);
                }
            } catch (error) {
                handleError(error);
            }
        };
        fetchListQuestions();
    }, [campaignId]);
    const { watch, handleSubmit } = methods;
    const { fields } = useFieldArray({
        control: methods.control,
        name: 'questions',
    });

    const onSubmit = handleSubmit(async (data) => {
        const payloads = (data?.questions ?? []).map((item) => {
            const { questionId } = item;
            if (
                ['checkbox', 'single_choice', 'dropdown'].includes(
                    item.questionType ?? ''
                )
            ) {
                return {
                    survey_id: Number(campaignId),
                    question_id: questionId,
                    answer_option_ids: item.answers,
                };
            }
        });
        try {
            const payloadObject = payloads[0];
            console.log(payloadObject);
            dispatch(setIsLoading(true));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const response = await compareQuestionData(payloadObject as any);
            if (response.success) {
                dispatch(setListQuestionDataFilter(response.data));
                dispatch(setIsLoading(false));
            }
        } catch (error) {
            handleError(error);
        } finally {
            dispatch(setIsLoading(false));
        }
    });
    return (
        <Box>
            <Typography
                sx={{
                    textAlign: 'left',
                    fontSize: '18px',
                    fontWeight: '600',
                    mb: 1,
                    mt: 2,
                }}
            >
                Lựa chọn câu hỏi & câu trả lời
            </Typography>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                {fields.map((field, index) => {
                    const questionId = watch(`questions.${index}.questionId`);
                    const questionType = watch(
                        `questions.${index}.questionType`
                    );

                    return (
                        <Box
                            key={field.id}
                            sx={{
                                borderBottom: '1px solid rgba(0, 0, 0, 0.6)',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center',
                                    mb: 2,
                                    mt: 2,
                                }}
                            >
                                <BaseSelectField
                                    size="small"
                                    name={`questions.${index}.questionId`}
                                    sx={{
                                        textAlign: 'left',
                                    }}
                                >
                                    {listQuestionsFilter?.map(
                                        (question, key) => (
                                            <MenuItem
                                                key={key}
                                                value={question.attributes.id}
                                            >
                                                {question.attributes.title}
                                            </MenuItem>
                                        )
                                    )}
                                </BaseSelectField>
                            </Box>

                            {questionId && (
                                <AnswersFetcher
                                    questionId={Number(questionId)}
                                    index={index}
                                    listQuestionsFilter={
                                        listQuestionsFilter ?? []
                                    }
                                />
                            )}
                            {['checkbox', 'single_choice', 'dropdown'].includes(
                                questionType ?? ''
                            ) &&
                                questionId && (
                                    <Box mb={2}>
                                        <BaseMultiCheckbox
                                            key={index}
                                            name={`questions.${index}.answers`}
                                            sxCheckbox={{
                                                '& .MuiFormControlLabel-label':
                                                    {
                                                        fontWeight: '500',
                                                    },
                                                '&.Mui-checked + .MuiFormControlLabel-label':
                                                    {
                                                        color: colors.main,
                                                        fontWeight: '500',
                                                    },
                                            }}
                                            options={
                                                watch(
                                                    `questions.${index}.availableAnswers`
                                                ) as {
                                                    value: string;
                                                    label: string;
                                                }[]
                                            }
                                        />
                                    </Box>
                                )}
                        </Box>
                    );
                })}
                <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                    <Button
                        sx={{
                            maxWidth: '120px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            borderRadius: '40px',
                        }}
                        type="submit"
                        variant="contained"
                    >
                        So sánh
                    </Button>
                </Box>
            </FormProvider>
        </Box>
    );
};

export default Compare;
